import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowBack, SupportAgent } from '@mui/icons-material';
import { Title, useLocaleState, useTranslate } from 'react-admin';
import {
  customerMessageText,
  getMySupportTicket,
  listMySupportTickets,
} from '../services/helpdeskTicketsService';
import { openHelpdeskConversation, toggleHelpdeskWidget } from '../components/misc/HelpdeskWidget';

const BRAND = '#AE256D';
const isActiveStatus = (status) => status === 'OPEN' || status === 'ON_HOLD';

const formatDate = (iso, locale, withTime = false) => {
  try {
    return new Date(iso).toLocaleString(locale === 'en' ? 'en-GB' : 'tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
      timeZone: 'Asia/Nicosia',
    });
  } catch {
    return '';
  }
};

const StatusChip = ({ status, translate }) => (
  <Chip
    size="small"
    label={translate(`supportTickets.status.${status}`, { _: status })}
    sx={{
      fontWeight: 600,
      flexShrink: 0,
      ...(isActiveStatus(status)
        ? { bgcolor: 'rgba(174,37,109,0.12)', color: BRAND }
        : { bgcolor: 'rgba(0,0,0,0.06)', color: 'text.secondary' }),
    }}
  />
);

/**
 * Kurumsal kullanıcının (mekan / organizatör / affiliate) kendi destek talepleri.
 * Açık talep → AppBar'daki destek widget'ında sohbet; kapanmış → salt okunur geçmiş.
 */
const SupportTicketsPage = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const uiLang = locale === 'en' ? 'en' : 'tr';
  const [tickets, setTickets] = useState(null);
  const [error, setError] = useState(null);
  const [openingHash, setOpeningHash] = useState(null);
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await listMySupportTickets();
      setTickets(res?.tickets || []);
    } catch {
      setTickets([]);
      setError(translate('supportTickets.loadError'));
    }
  }, [translate]);

  useEffect(() => {
    load();
  }, [load]);

  const openTicket = async (item) => {
    if (openingHash) return;
    setOpeningHash(item.ticketHash);
    try {
      const thread = await getMySupportTicket(item.ticketHash);
      if (isActiveStatus(thread.status)) openHelpdeskConversation(thread);
      else setHistory(thread);
    } catch {
      setError(translate('supportTickets.loadError'));
    } finally {
      setOpeningHash(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 880 }}>
      <Title title={translate('menu.support_tickets')} />

      {history ? (
        <Card>
          <CardContent>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setHistory(null)}
              sx={{ textTransform: 'none', color: BRAND, mb: 1, px: 0 }}
            >
              {translate('menu.support_tickets')}
            </Button>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {history.subject}
              </Typography>
              <StatusChip status={history.status} translate={translate} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate('supportTickets.historyNote')}
            </Typography>
            <Stack spacing={1.25}>
              {(history.messages || []).map((m) => (
                <Box
                  key={m.id}
                  sx={{
                    maxWidth: '82%',
                    alignSelf: m.isStaff ? 'flex-start' : 'flex-end',
                    bgcolor: m.isStaff ? 'rgba(0,0,0,0.05)' : BRAND,
                    color: m.isStaff ? 'text.primary' : '#fff',
                    borderRadius: 2,
                    px: 1.75,
                    py: 1.25,
                  }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 700, opacity: 0.75 }}>
                    {m.isStaff
                      ? m.staffName || translate('menu.support')
                      : translate('supportTickets.you')}
                  </Typography>
                  <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
                    {customerMessageText(m, history.language, uiLang)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, opacity: 0.6, mt: 0.5 }}>
                    {formatDate(m.createdAt, uiLang, true)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              {translate('supportTickets.subtitle')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<SupportAgent />}
              onClick={toggleHelpdeskWidget}
              sx={{ textTransform: 'none', bgcolor: BRAND, '&:hover': { bgcolor: '#961F5E' } }}
            >
              {translate('supportTickets.newRequest')}
            </Button>
          </Stack>

          {error ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {!tickets ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={28} sx={{ color: BRAND }} />
            </Box>
          ) : tickets.length === 0 ? (
            <Card>
              <CardContent sx={{ py: 5, textAlign: 'center' }}>
                <Typography color="text.secondary">{translate('supportTickets.empty')}</Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {tickets.map((item) => {
                const preview = item.lastMessage
                  ? customerMessageText(item.lastMessage, item.language, uiLang)
                  : '';
                return (
                  <Card key={item.ticketId} variant="outlined">
                    <CardActionArea onClick={() => openTicket(item)} disabled={!!openingHash}>
                      <CardContent>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 600 }} noWrap>
                            {item.subject}
                          </Typography>
                          <StatusChip status={item.status} translate={translate} />
                        </Stack>
                        {preview ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.75,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {preview}
                          </Typography>
                        ) : null}
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mt: 1 }}
                        >
                          <Typography variant="caption" color="text.disabled">
                            #{item.ticketHash} · {formatDate(item.updatedAt, uiLang)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: BRAND, fontWeight: 700 }}>
                            {openingHash === item.ticketHash ? (
                              <CircularProgress size={12} sx={{ color: BRAND }} />
                            ) : isActiveStatus(item.status) ? (
                              translate('supportTickets.continueChat')
                            ) : (
                              translate('supportTickets.viewHistory')
                            )}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default SupportTicketsPage;
